/**
 * Which events are sinks, and what to scan in them (docs/event-model.md §6):
 * a network event's URL, header values, and body; a messaging event's
 * arguments. The engine adds the guest's untruncated payloads (`RawEvent.scan`).
 */
import {
  type AuditEvent,
  CANARY_ENCODINGS,
  CANARY_HIT_FIELDS,
  type CanaryHit,
  type ScanPart,
  type SerializedValue,
} from "../events";
import { decodeBase64 } from "./encode";
import { type CanaryScanner, type SinkPart, scanPayload } from "./scan";
import type { CanaryRegistry } from "./types";

/** Every string in a serialized value (keys too), plus decoded byte arrays. */
export function serializedStrings(value: SerializedValue, out: string[] = []): string[] {
  if (typeof value === "string") out.push(value);
  else if (value !== null && typeof value === "object") {
    if ("$str" in value) out.push(value.$str);
    else if ("$bytes" in value) out.push(decodeBase64(value.$bytes));
    else if ("$arr" in value) for (const item of value.$arr) serializedStrings(item, out);
    else if ("$obj" in value) {
      for (const [key, item] of Object.entries(value.$obj)) {
        out.push(key);
        serializedStrings(item, out);
      }
    }
  }
  return out;
}

/** True for events whose payload leaves the page: network requests and messages. */
export const isSinkEvent = (event: AuditEvent): boolean =>
  event.network !== undefined || event.category === "messaging";

/**
 * The parts of a sink event to scan, with the guest's full payloads
 * (`extra`) appended; null for events that aren't sinks.
 */
export function sinkParts(event: AuditEvent, extra: readonly ScanPart[] = []): SinkPart[] | null {
  if (!isSinkEvent(event)) return null;
  const parts: SinkPart[] = [];
  const { network } = event;
  if (network) {
    parts.push({ field: "url", text: network.url });
    for (const value of Object.values(network.headers ?? {})) {
      parts.push({ field: "header", text: value });
    }
    if (network.body !== undefined) parts.push({ field: "body", text: network.body });
  } else {
    const text = event.args.flatMap((a) => serializedStrings(a)).join("\n");
    parts.push({ field: "args", text });
  }
  return [...parts, ...extra];
}

/** The canaries in a sink event's payload (and the guest's full payloads); none for other events. */
export function scanEvent(
  scanner: CanaryScanner,
  event: AuditEvent,
  extra: readonly ScanPart[] = [],
): CanaryHit[] {
  const parts = sinkParts(event, extra);
  return parts ? scanPayload(scanner, parts) : [];
}

/**
 * A one-line description of an event's canary hits for the Timeline badge:
 * `document.cookie:session_id in url (base64); c3 in body (raw, partial)`.
 * Canaries are named by source when `registry` knows them, else by id.
 */
export function describeHits(hits: readonly CanaryHit[], registry?: CanaryRegistry): string {
  const ids = [...new Set(hits.map((h) => h.canary))];
  return ids
    .map((id) => {
      const own = hits.filter((h) => h.canary === id);
      const name = registry?.canaries.find((c) => c.id === id)?.source ?? id;
      const fields = CANARY_HIT_FIELDS.filter((f) => own.some((h) => h.field === f));
      const notes: string[] = CANARY_ENCODINGS.filter((e) => own.some((h) => h.encoding === e));
      if (own.every((h) => h.partial)) notes.push("partial");
      return `${name} in ${fields.join(", ")} (${notes.join(", ")})`;
    })
    .join("; ");
}
